import { messageListAriaLabel } from './MailListContent.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import Draggable from 'owa-dnd/lib/components/Draggable';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import getMailListRowDragData from 'owa-mail-list-store/lib//utils/getMailListRowDragData';
import getMailListTableProps from '../utils/getMailListTableProps';
import isReadingPaneBottom from 'owa-mail-reading-pane-store/lib/utils/isReadingPaneBottom';
import loadMoreInTable from 'owa-mail-list-actions/lib/actions/table/loadMoreInTable';
import LoadMoreListView from 'owa-loadmore-listview/lib/components/LoadMoreListView';
import MailListGroupHeader from './MailListGroupHeader';
import MailListItemDataProvider from './MailListItemDataProvider';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import MailListLoading from './MailListLoading';
import MailListTableProps from '../utils/types/MailListTableProps';
import mailStore from 'owa-mail-store/lib/store/Store';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';

import updateMailItemHeights from '../utils/updateMailItemHeights';
import { computed } from 'mobx';
import { FocusedInboxRollup, shouldShowFocusedRollup } from 'owa-mail-focused-inbox-rollup-view';
import { focusedInboxRollupStore } from 'owa-mail-focused-inbox-rollup-store';
import { getCanTableLoadMore } from 'owa-mail-table-loading-actions';
import { getCommands } from 'owa-mail-hotkeys';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isGroupTableQuery } from 'owa-group-utils';
import { keydownHandler } from 'owa-hotkeys';
import { lazyMarkAsReadInTable } from 'owa-mail-mark-read-actions';
import { MessageAdList } from 'owa-mail-messagead-list-view';
import { observer } from 'mobx-react';
import { resetFocus, tabIndex } from 'owa-mail-focus-manager';
import {
    doesUserHaveSharedFolderPermissionFor,
    MenuItemType,
} from 'owa-mail-filterable-menu-behavior';
import {
    keyboardMultiSelectRow,
    selectRowInDirection,
    toggleSelectAllRows,
    singleSelectRow,
    resetSelection,
} from 'owa-mail-actions';
import {
    getMailListGroupHeader,
    getMailListGroupHeaderGenerator,
} from 'owa-mail-list-group-headers';
import {
    lazyForwardConversation,
    lazyReplyToConversation,
    lazyForwardItem,
    lazyReplyToItem,
} from 'owa-mail-message-actions';
import {
    getMailListRowDragPreview,
    MAIL_ITEM_DRAG_XOFFSET,
    MAIL_ITEM_DRAG_YOFFSET,
} from '../utils/mailListDragUtil';
import {
    getFocusedFilterForTable,
    getIsEverythingSelectedInTable,
    listViewStore,
    RowOperation,
    TableQueryType,
    MailRowDataPropertyGetter,
    TableView,
    SelectionDirection,
    shouldSuppressServerMarkReadOnReplyOrForward,
} from 'owa-mail-list-store';
import {
    lazyArchiveMailListRows,
    lazyDeleteMailListRows,
    lazyEmptyTableView,
    lazyMarkRowsJunkNotJunk,
    lazyToggleRowsFlagState,
} from 'owa-mail-triage-action';
import { GetStartedList } from 'owa-get-started';
import { isFocusedInboxEnabled } from 'owa-mail-list-actions/lib/utils/FocusedInboxUtil';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';

const styles = require('./MailList.scss');
const classNames = require('classnames');

export interface MailListContentProps {
    isFocusOnMailList: () => boolean;
    isFocusInMailList: () => boolean;
    tableViewId: string;
    onScroll?: (ev?: any) => void;
    shouldShowMessageAdList: boolean;
    styleSelectorAsPerUserSettings: string;
}

@observer
export default class MailListContent extends React.Component<MailListContentProps, {}> {
    private mailListViewContent: HTMLDivElement;
    private listView: LoadMoreListView;
    private lastShowListPane;
    private lastTableViewId;
    private mailListGroupHeaderGenerator;
    private propagateScrollingTask: NodeJS.Timer; // Timer to delay updating tableView values (that would cause a rerender) on user scroll so that we only update when user is done scrolling

    componentDidMount() {
        // Trigger focus if necessary after mount
        this.focusOnSelfOnComponentUpdate();
    }

    componentWillUpdate() {
        const tableView = this.getTableView();

        // tableView will be null if user deleted the folder while it was selected
        if (tableView && tableView.currentRowOperation == RowOperation.RowMove) {
            // If we're in the process of doing a row move, we must store the current scroll position before component
            // updates, as React will handle row moves in a way that will cause the listview to jump.
            this.listView.storeScrollPosition();
        }
    }

    componentDidUpdate(prevProps: MailListContentProps) {
        const tableView = this.getTableView();

        // tableView will be null if user deleted the folder while it was selected
        if (tableView && tableView.currentRowOperation == RowOperation.RowMove) {
            // If we're in the process of doing a row move, we must restore the scroll position after component updates,
            // as React will handle row moves in a way that will cause the listview to jump.
            this.listView.restoreSavedScrollPosition();
        }

        // Trigger focus if necessary after component update
        this.focusOnSelfOnComponentUpdate();
    }

    componentWillUnmount() {
        if (this.props.isFocusInMailList) {
            // Reset focus such that it can be restored when component is updated
            resetFocus();
        }
    }

    private focusOnSelfOnComponentUpdate = () => {
        // If the listview loads without cached content, only MailList is displayed.
        // When the mail list content gets rendered layer, it needs to transfer focus from MailList to MailListContent
        // to support keyboard navigation.
        window.requestAnimationFrame(() => {
            if (this.props.isFocusOnMailList()) {
                this.trySetFocus();
            }
        });
    };

    /**
     * Handles scroll on list pane hidden / shown
     */
    private handleScrollPositionOnShowHideListPane = (currentTableViewId: string) => {
        const currentShowListPane = mailStore.mailModule.showListPane;

        // When list pane changes from shown to hidden, it means we have gone into immersive reading pane and we need to
        // save current scroll position for if user exits and goes back to same list pane (in same table). When user
        // exits immersive reading pane and returns to the same table / previously shown list pane which changes list pane
        // from hidden to shown, we need to restore scroll position to where user was before going into immersive.
        // If user goes to a different table from immersive, then we do not restore scroll position as it should be reset to top.
        if (
            this.lastShowListPane !== null &&
            this.lastShowListPane !== currentShowListPane &&
            this.lastTableViewId === currentTableViewId
        ) {
            if (currentShowListPane) {
                this.listView.restoreSavedScrollPosition();
            } else {
                this.listView.storeScrollPosition();
            }

            resetFocus();
        }

        this.lastShowListPane = currentShowListPane;
        this.lastTableViewId = currentTableViewId;
    };

    private getCanCurrentTableLoadMore = (): boolean => {
        return getCanTableLoadMore(this.getTableView());
    };

    @computed.struct
    get mailListTableProps() {
        return getMailListTableProps(this.props.tableViewId);
    }

    private renderListViewContent(): JSX.Element {
        const tableView = this.getTableView();
        const listProps = this.mailListTableProps;
        this.mailListGroupHeaderGenerator = getMailListGroupHeaderGenerator(tableView);

        // Ensure scroll position is maintained on show/hide listview
        this.handleScrollPositionOnShowHideListPane(tableView.id);

        // Warning: this.getScrollRegionClassNames() must be assigned to LoadMoreListView in order for the LoadMoreListView
        // to get proper onscroll events. Please keep this in mind when trying to refactor the DOM layers here.
        const loadMoreListView = (
            <div
                tabIndex={0}
                role="listbox"
                aria-label={loc(messageListAriaLabel)}
                ref={ref => (this.mailListViewContent = ref)}
                className={styles.wrapperDiv}>
                <LoadMoreListView
                    ref={ref => (this.listView = ref)}
                    className={this.getScrollRegionClassNames()}
                    dataSourceId={tableView.id}
                    itemIds={tableView.rowKeys}
                    onScroll={this.onScroll}
                    onRenderRow={this.createMailListItem}
                    onRenderHeader={this.createHeader}
                    onLoadMoreRows={this.onLoadMoreRows}
                    isLoadRowsInProgress={tableView.isLoading}
                    getCanLoadMore={this.getCanCurrentTableLoadMore}
                    currentLoadedIndex={tableView.currentLoadedIndex}
                    guardPageCount={3}
                    loadingComponent={this.renderLoadingSpinner()}
                    rowWrapperClass={styles.rowWrapper}
                    preRowsComponent={
                        <div>
                            {this.props.shouldShowMessageAdList && (
                                <MessageAdList
                                    loadInOtherPivot={
                                        getFocusedFilterForTable(tableView) ==
                                        FocusedViewFilter.Other
                                    }
                                    loadInEmptyPivot={false}
                                    loadInItemView={
                                        tableView.tableQuery.listViewType ==
                                        ReactListViewType.Message
                                    }
                                />
                            )}
                        </div>
                    }
                    postRowsComponent={
                        <div>
                            {this.renderGetStartedPane(tableView)}
                            {this.renderSpinnerPlaceholder()}
                        </div>
                    }
                    listProps={listProps}
                />
            </div>
        );

        return loadMoreListView;
    }

    private renderGetStartedPane = tableView => {
        return (
            isFeatureEnabled('getStarted-ListView') &&
            tableView.tableQuery.folderId == folderNameToId('inbox') &&
            tableView.tableQuery.type != TableQueryType.Search &&
            (getFocusedFilterForTable(tableView) == FocusedViewFilter.Focused ||
                !isFocusedInboxEnabled()) && <GetStartedList />
        );
    };

    private onScroll = (event: any, scrollTop: number, offsetHeight: number) => {
        this.props.onScroll(event);
        const rowKeys = this.getTableView().rowKeys;

        // Virtualization will only kick in after a certain amount of rows have been loaded in
        if (isFeatureEnabled('tri-virtualizedListView') && rowKeys.length > 175) {
            if (this.propagateScrollingTask) {
                clearTimeout(this.propagateScrollingTask);
                this.propagateScrollingTask = null;
            }

            this.propagateScrollingTask = setTimeout(() => {
                this.propagateScrollingTask = null;
                updateMailItemHeights(rowKeys, scrollTop, offsetHeight);
            }, 150);
        }
    };

    private onLoadMoreRows = () => {
        loadMoreInTable(this.getTableView());
    };

    private getScrollRegionClassNames(): string {
        return classNames('customScrollBar', styles.outerScrollContainer);
    }

    private createMailListItem = (
        rowKey: string,
        itemIndex: number,
        listProps: MailListTableProps
    ) => {
        return (
            <MailListItemDataProvider
                key={rowKey}
                rowKey={rowKey}
                mailListTableProps={listProps}
                resetListViewContentTabIndex={this.resetListViewContentTabIndex}
                isFocusInMailList={this.props.isFocusInMailList}
                styleSelectorAsPerUserSettings={this.props.styleSelectorAsPerUserSettings}
            />
        );
    };

    private renderLoadingSpinner(): JSX.Element {
        // Do not render the loading spinner if the table cannot load more. If the table is in loading state but cannot load more,
        // it indicates it's doing a reload in which case we don't want to show the load more spinner.
        return this.getCanCurrentTableLoadMore() ? <MailListLoading /> : null;
    }

    private renderSpinnerPlaceholder(): JSX.Element {
        const tableView = this.getTableView();

        // Do not show the placeholder if we're loading and showing the spinner, or this table
        // cannot load more which means the spinner won't ever appear.
        if (tableView.isLoading || !this.getCanCurrentTableLoadMore()) {
            return null;
        }

        // Create an element to render after all rows that take up the same space as the loading indicator,
        // so that the height of the scroll region doesn't change when loading indicator appears
        return <div className={styles.spinnerPlaceHolder} />;
    }

    /**
     * Clear tabindex of container such that container is not in a sequential
     * keyboard navigation when an item is selected in listview
     */
    private resetListViewContentTabIndex = () => {
        if (this.mailListViewContent.getAttribute('tabindex') == tabIndex.sequentialIndex) {
            this.mailListViewContent.setAttribute('tabindex', tabIndex.nonSequentialIndex);
        }
    };

    private createHeader = (previousRowKey: string, currentRowKey: string) => {
        const headers: JSX.Element[] = [];
        const headerText = getMailListGroupHeader(
            previousRowKey,
            currentRowKey,
            this.getTableView(),
            this.mailListGroupHeaderGenerator
        );

        if (headerText) {
            headers.push(
                <MailListGroupHeader
                    key={`gh_${headerText}`}
                    headerText={headerText}
                    groupHeaderStylesAsPerUserSettings={this.props.styleSelectorAsPerUserSettings}
                />
            );
        }

        if (shouldShowFocusedRollup(previousRowKey, currentRowKey)) {
            headers.push(
                <FocusedInboxRollup
                    key="rollup"
                    rollupState={focusedInboxRollupStore}
                    tableViewId={this.props.tableViewId}
                    styleSelectorAsPerUserSettings={this.props.styleSelectorAsPerUserSettings}
                />
            );
        }

        return headers.length > 0 ? <div key={'header_' + currentRowKey}>{headers}</div> : null;
    };

    render() {
        return (
            <Draggable
                canDrag={this.canDrag}
                getDragData={getMailListRowDragData}
                getDragPreview={getMailListRowDragPreview}
                xOffset={MAIL_ITEM_DRAG_XOFFSET}
                yOffset={MAIL_ITEM_DRAG_YOFFSET}
                classNames={styles.draggableDiv}>
                {this.renderListViewContent()}
            </Draggable>
        );
    }

    public trySetFocus = (): boolean => {
        if (this.mailListViewContent) {
            this.mailListViewContent.focus();

            return true;
        }
        return false;
    };

    private getTableView = (): TableView => {
        return listViewStore.tableViews.get(this.props.tableViewId);
    };

    /**
     * Hot keys
     */
    @keydownHandler('down', false /*stopPropagation*/)
    selectNextRow() {
        const tableView = this.getTableView();
        selectRowInDirection(
            tableView,
            SelectionDirection.Next,
            MailListItemSelectionSource.KeyboardUpDown
        );
    }

    @keydownHandler('shift+down', false /*stopPropagation*/)
    keyboardShiftMultiSelectNextRow() {
        keyboardMultiSelectRow(
            this.getTableView(),
            MailListItemSelectionSource.KeyboardUpDown,
            SelectionDirection.Next,
            false /* isCtrlDown */
        );
    }

    @keydownHandler('shift+ctrl+down', false /*stopPropagation*/)
    keyboardCtrlShiftMultiSelectNextRow() {
        keyboardMultiSelectRow(
            this.getTableView(),
            MailListItemSelectionSource.KeyboardUpDown,
            SelectionDirection.Next,
            true /* isCtrlDown */
        );
    }

    @keydownHandler('up', false /*stopPropagation*/)
    selectPreviousRow() {
        const tableView = this.getTableView();
        selectRowInDirection(
            tableView,
            SelectionDirection.Previous,
            MailListItemSelectionSource.KeyboardUpDown
        );
    }

    @keydownHandler('shift+up', false /*stopPropagation*/)
    multiselectPrevRow() {
        keyboardMultiSelectRow(
            this.getTableView(),
            MailListItemSelectionSource.KeyboardUpDown,
            SelectionDirection.Previous,
            false /* isCtrlDown */
        );
    }

    @keydownHandler('shift+ctrl+up', false /*stopPropagation*/)
    keyboardCtrlShiftMultiSelectPreviousRow() {
        keyboardMultiSelectRow(
            this.getTableView(),
            MailListItemSelectionSource.KeyboardUpDown,
            SelectionDirection.Previous,
            true /* isCtrlDown */
        );
    }

    @keydownHandler(getCommands().selectAll, true /* stopPropagation */)
    selectAll() {
        const tableView = this.getTableView();
        const isEverythingSelectedBeforeToggle = getIsEverythingSelectedInTable(tableView);
        if (!isEverythingSelectedBeforeToggle) {
            toggleSelectAllRows(tableView);
        }
    }

    // deselectAll is tied to esc key in certain key sets
    // We stop propagation only when we toggleCheckAll, else
    // we let the event to propagate
    @keydownHandler(getCommands().deselectAll, true /* stopPropagation */)
    deselectAll() {
        const tableView = this.getTableView();

        const isEverythingSelectedBeforeToggle = getIsEverythingSelectedInTable(tableView);
        if (isEverythingSelectedBeforeToggle) {
            toggleSelectAllRows(tableView);
        } else {
            resetSelection(tableView, MailListItemSelectionSource.Reset);
        }
    }

    @keydownHandler(getCommands().home, true /* stopPropagation */)
    home() {
        const tableView = this.getTableView();

        if (tableView.rowKeys.length > 0) {
            singleSelectRow(
                tableView,
                tableView.rowKeys[0], // Select first row
                true /* isUserNavigation */,
                MailListItemSelectionSource.KeyboardHome
            );
        }
    }

    @keydownHandler(getCommands().openMail)
    openMail() {
        this.openEmail(MailListItemSelectionSource.KeyboardEnter);
    }

    @keydownHandler(getCommands().openMailInPopout)
    openMailInPopout() {
        this.openEmail(MailListItemSelectionSource.KeyboardShiftEnter);
    }

    private openEmail(selectionSource: MailListItemSelectionSource) {
        const tableView = this.getTableView();
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
        if (selectedRowKeys.length == 1) {
            singleSelectRow(
                tableView,
                selectedRowKeys[0],
                true /* isUserNavigation */,
                selectionSource
            );
        }
    }

    @keydownHandler(getCommands().archiveMail)
    archiveRow() {
        const tableView = this.getTableView();
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
        if (selectedRowKeys.length == 0) {
            return;
        }

        // archiving is disabled for groups
        if (isGroupTableQuery(tableView.tableQuery)) {
            return;
        }

        lazyArchiveMailListRows.importAndExecute(selectedRowKeys, tableView, 'Keyboard');
    }

    @keydownHandler(getCommands().markAsJunk)
    markAsJunk() {
        const tableView = this.getTableView();
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
        if (selectedRowKeys.length == 0) {
            return;
        }

        // mark as junk is disabled for groups
        if (isGroupTableQuery(tableView.tableQuery)) {
            return;
        }

        lazyMarkRowsJunkNotJunk.importAndExecute(
            selectedRowKeys,
            tableView,
            true /* isJunkValueToSet */,
            'Keyboard' /* actionSource */
        );
    }

    @keydownHandler(getCommands().deleteMail)
    deleteRow() {
        this.deleteSelectedRow();
    }

    @keydownHandler(getCommands().softDeleteMail)
    softDeleteRow() {
        this.deleteSelectedRow(true /*isSoftDelete*/);
    }

    @keydownHandler(getCommands().markAsRead)
    markAsReadRow() {
        this.markAsReadUnreadInternal(true /* isReadValueToSet */);
    }

    @keydownHandler(getCommands().markAsUnread)
    markAsUnreadRow() {
        this.markAsReadUnreadInternal(false /* isReadValueToSet */);
    }

    private markAsReadUnreadInternal = (isReadValueToSet: boolean) => {
        const tableView = this.getTableView();
        const isInVirtualSelectAllMode = tableView.isInVirtualSelectAllMode;
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];

        if (!isInVirtualSelectAllMode && selectedRowKeys.length == 0) {
            return;
        }

        const rowKeysToActOn = isInVirtualSelectAllMode ? [] : selectedRowKeys;
        const exclusionList = isInVirtualSelectAllMode
            ? tableView.virutalSelectAllExclusionList
            : [];

        lazyMarkAsReadInTable.importAndExecute(
            'Keyboard',
            exclusionList,
            isInVirtualSelectAllMode /* isActingOnAllItemsInTable */,
            isReadValueToSet,
            rowKeysToActOn,
            tableView
        );
    };

    @keydownHandler(getCommands().toggleFlag)
    toggleFlag() {
        const tableView = this.getTableView();
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
        if (selectedRowKeys.length == 0) {
            return;
        }

        // Disable flag toggling for shared folders if the user doesn't have the permission
        if (!doesUserHaveSharedFolderPermissionFor(MenuItemType.Flag)) {
            return;
        }

        // toggle flag is disabled for groups
        if (isGroupTableQuery(tableView.tableQuery)) {
            return;
        }

        lazyToggleRowsFlagState.importAndExecute(selectedRowKeys, tableView.id, 'Keyboard');
    }

    @keydownHandler(getCommands().replyAll)
    replyAllToRow() {
        this.replyReplyAllInternal(true /* isReplyAll */);
    }

    @keydownHandler(getCommands().reply)
    replyToRow() {
        const tableView = this.getTableView();
        // Reply (little-r) is disabled for groups
        if (isGroupTableQuery(tableView.tableQuery)) {
            return;
        }

        this.replyReplyAllInternal(false /* isReplyAll */);
    }

    private replyReplyAllInternal = (isReplyAll: boolean) => {
        const tableView = this.getTableView();
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
        // Only support reply (all) to a single row.
        // If it's multi selected, take no action
        if (selectedRowKeys.length != 1) {
            return;
        }
        const suppressServerMarkReadOnReplyOrForward = shouldSuppressServerMarkReadOnReplyOrForward(
            tableView
        );
        const rowId = MailRowDataPropertyGetter.getRowClientItemId(selectedRowKeys[0], tableView);
        let instrumentationContexts;
        if (tableView.tableQuery.type == TableQueryType.Search) {
            instrumentationContexts = getInstrumentationContextsFromTableView(
                [selectedRowKeys[0]],
                tableView
            );
        }
        switch (tableView.tableQuery.listViewType) {
            case ReactListViewType.Conversation:
                lazyReplyToConversation.importAndExecute(
                    rowId,
                    isReplyAll,
                    'Keyboard',
                    instrumentationContexts,
                    isReadingPaneBottom() /*useFullCompose*/,
                    suppressServerMarkReadOnReplyOrForward
                );
                break;

            case ReactListViewType.Message:
                lazyReplyToItem.importAndExecute(
                    rowId,
                    isReplyAll,
                    instrumentationContexts,
                    'Keyboard',
                    suppressServerMarkReadOnReplyOrForward
                );
        }
    };

    @keydownHandler(getCommands().forward)
    forwardRow() {
        const tableView = this.getTableView();
        const selectedRowKeys = [...tableView.selectedRowKeys.keys()];

        // Only support forward a single row.
        // If it's multi selected, take no action
        if (selectedRowKeys.length != 1) {
            return;
        }

        const suppressServerMarkReadOnReplyOrForward = shouldSuppressServerMarkReadOnReplyOrForward(
            tableView
        );
        const rowId = MailRowDataPropertyGetter.getRowClientItemId(selectedRowKeys[0], tableView);
        let instrumentationContexts;
        if (tableView.tableQuery.type == TableQueryType.Search) {
            instrumentationContexts = getInstrumentationContextsFromTableView(
                [selectedRowKeys[0]],
                tableView
            );
        }
        switch (tableView.tableQuery.listViewType) {
            case ReactListViewType.Conversation:
                lazyForwardConversation.importAndExecute(
                    rowId,
                    'Keyboard',
                    instrumentationContexts,
                    suppressServerMarkReadOnReplyOrForward
                );
                break;
            case ReactListViewType.Message:
                lazyForwardItem.importAndExecute(
                    rowId,
                    'Keyboard',
                    instrumentationContexts,
                    suppressServerMarkReadOnReplyOrForward
                );
                break;
        }
    }

    private canDrag = () => {
        return getMailListTableProps(this.props.tableViewId).canDragFromTable;
    };

    /**
     * Delete seleted row in the current folder
     * @param isExplicitSoftDelete flag to indicate whether disposal type is soft delete
     */
    private async deleteSelectedRow(isExplicitSoftDelete?: boolean): Promise<void> {
        const tableView = this.getTableView();

        // Disable row deletion in groups or
        // if the row is in a shared folder and user doesn't have delete permissions
        if (
            tableView.tableQuery.type == TableQueryType.Group ||
            !doesUserHaveSharedFolderPermissionFor(MenuItemType.Delete)
        ) {
            return;
        }

        if (tableView.isInVirtualSelectAllMode) {
            // When in virtual select all mode, i.e select all mode in non-search,
            // delete all in the folder
            await lazyEmptyTableView.importAndExecute(
                tableView,
                isExplicitSoftDelete,
                'Keyboard',
                tableView.virutalSelectAllExclusionList
            );
        } else {
            const selectedRowKeys = [...tableView.selectedRowKeys.keys()];
            if (selectedRowKeys.length > 0) {
                await lazyDeleteMailListRows.importAndExecute(
                    selectedRowKeys,
                    tableView.id,
                    isExplicitSoftDelete,
                    'Keyboard'
                );
            }
        }

        if (tableView.rowKeys.length == 0) {
            // Set focus on the container such that it doesn't get
            // lost when the deleted item is the last one in the current folder
            resetFocus();
        }
    }
}
